// Feb-2026 · Capacitación PLD/FT — Vista de la entidad (Disposición 52ª A-VI).
// Muestra sesiones asignadas por FELVORA, inscribe empleados desde el padrón,
// reposiciones pendientes, constancias y programa anual con semáforo.
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  GraduationCap, Users, ClipboardList, FileText, Download, Loader2,
  CalendarClock, RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck, UserCheck,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";


const COLOR_SEMAFORO = {
  verde:    "bg-emerald-100 text-emerald-700 border-emerald-300",
  amarillo: "bg-amber-100 text-amber-800 border-amber-300",
  naranja:  "bg-orange-100 text-orange-800 border-orange-300",
  rojo:     "bg-red-100 text-red-700 border-red-300",
  gris:     "bg-slate-100 text-slate-600 border-slate-300",
};

function SemaforoBadge({ estado, color, dias, aplica, motivo }) {
  const dur = dias != null ? ` · ${dias >= 0 ? `${dias} días` : `${Math.abs(dias)} días vencido`}` : "";
  const sufijoDias = aplica === false ? "" : dur;
  return (
    <Badge
      className={`border ${COLOR_SEMAFORO[color] || COLOR_SEMAFORO.gris}`}
      data-testid="semaforo-programa"
      title={motivo || undefined}
    >
      {estado}{sufijoDias}
    </Badge>
  );
}

function EstatusSesion({ e }) {
  const map = {
    Programada: "bg-blue-100 text-blue-700",
    Impartida:  "bg-emerald-100 text-emerald-700",
    Cancelada:  "bg-slate-100 text-slate-500",
  };
  return <Badge className={map[e] || "bg-slate-100"}>{e}</Badge>;
}

export default function Capacitacion() {
  const { user } = useAuth();
  const puedeInscribir = ["admin_entidad", "oficial_cumplimiento", "tecnico"].includes(user?.role);
  const esEmpleadoComun = !["admin_entidad", "oficial_cumplimiento", "tecnico", "super_admin", "auditor_externo"].includes(user?.role);

  const [tab, setTab] = useState("sesiones");
  const [sesiones, setSesiones] = useState([]);
  const [reposiciones, setReposiciones] = useState([]);
  const [constancias, setConstancias] = useState([]);
  const [misConstancias, setMisConstancias] = useState([]);
  const [limite, setLimite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(null);
  const [descargandoPrograma, setDescargandoPrograma] = useState(false);

  // Inscripción
  const [showInscribir, setShowInscribir] = useState(false);
  const [sesionInscripcion, setSesionInscripcion] = useState(null);
  const [padron, setPadron] = useState([]);
  const [seleccion, setSeleccion] = useState(new Set());
  const [enviandoInsc, setEnviandoInsc] = useState(false);

  const loadTodo = useCallback(async () => {
    setLoading(true);
    try {
      const anio = new Date().getFullYear();
      const [rs, rr, rc, rl, rmc] = await Promise.all([
        api.get("/capacitacion/sesiones"),
        api.get("/capacitacion/reposiciones"),
        api.get("/capacitacion/constancias"),
        api.get(`/capacitacion/programa-anual/fecha-limite?anio=${anio}`),
        esEmpleadoComun ? api.get("/capacitacion/mis-constancias") : Promise.resolve({ data: [] }),
      ]);
      setSesiones(rs.data || []);
      setReposiciones(rr.data || []);
      setConstancias(rc.data || []);
      setLimite(rl.data || null);
      setMisConstancias(rmc.data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [esEmpleadoComun]);

  useEffect(() => { loadTodo(); }, [loadTodo]);

  const abrirInscripcion = async (sesion) => {
    setSesionInscripcion(sesion);
    setSeleccion(new Set());
    try {
      const { data } = await api.get("/capacitacion/empleados-inscripcion");
      setPadron(data || []);
      setShowInscribir(true);
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSel = (id) => {
    const n = new Set(seleccion);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSeleccion(n);
  };
  const enviarInscripcion = async () => {
    if (seleccion.size === 0) { toast.error("Selecciona al menos un empleado."); return; }
    setEnviandoInsc(true);
    try {
      const { data } = await api.post("/capacitacion/inscripciones", {
        sesion_id: sesionInscripcion.id, empleado_ids: Array.from(seleccion),
      });
      toast.success(`Inscritos: ${data.inscritos} · duplicados omitidos: ${data.omitidos_duplicados}`);
      setShowInscribir(false); setSesionInscripcion(null); setSeleccion(new Set());
      await loadTodo();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnviandoInsc(false); }
  };

  const descargarConstancia = async (c) => {
    setDescargando(c.id);
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/capacitacion/constancias/${c.id}/descargar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || "No se pudo descargar.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `constancia_${c.folio}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
    finally { setDescargando(null); }
  };

  const descargarProgramaAnual = async () => {
    setDescargandoPrograma(true);
    try {
      const anio = new Date().getFullYear();
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/capacitacion/programa-anual/descargar?anio=${anio}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo generar el programa anual.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `programa_anual_capacitacion_${anio}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
    finally { setDescargandoPrograma(false); }
  };

  const sesionesVisibles = useMemo(() => {
    if (esEmpleadoComun) {
      // El empleado común solo ve sesiones donde figura como inscrito — filtrado servidor no aplica aquí,
      // por diseño mostramos igual las sesiones de su entidad (para transparencia).
      return sesiones;
    }
    return sesiones;
  }, [sesiones, esEmpleadoComun]);

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="page-capacitacion">
      <PageHeader
        icon={<GraduationCap className="h-5 w-5" />}
        title="Capacitación PLD/FT"
        subtitle="Sesiones, inscripciones, constancias y programa anual (Disposición 52ª A-VI)."
      />

      {/* Semáforo del programa anual */}
      {limite && (
        <Alert className="border-slate-200 bg-slate-50">
          <CalendarClock className="h-4 w-4 text-slate-500" />
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>
              <strong>Envío del Programa Anual a CNBV {limite.anio}:</strong> fecha límite {limite.fecha_limite} (primeros 15 días hábiles de enero).
            </span>
            <SemaforoBadge estado={limite.estado} color={limite.color} dias={limite.dias_restantes} aplica={limite.aplica} motivo={limite.motivo} />
            <Button size="sm" variant="outline" onClick={descargarProgramaAnual} disabled={descargandoPrograma} data-testid="btn-descargar-programa">
              {descargandoPrograma ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
              Descargar programa anual {limite.anio}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="sesiones" data-testid="tab-sesiones"><ClipboardList className="mr-1 h-4 w-4" /> Sesiones</TabsTrigger>
          <TabsTrigger value="reposiciones" data-testid="tab-reposiciones"><RefreshCw className="mr-1 h-4 w-4" /> Reposiciones{reposiciones.filter(r=>r.estatus==="Pendiente").length ? ` (${reposiciones.filter(r=>r.estatus==="Pendiente").length})` : ""}</TabsTrigger>
          <TabsTrigger value="constancias" data-testid="tab-constancias"><FileText className="mr-1 h-4 w-4" /> Constancias</TabsTrigger>
          {esEmpleadoComun && (
            <TabsTrigger value="mis-constancias" data-testid="tab-mis-constancias"><ShieldCheck className="mr-1 h-4 w-4" /> Mis constancias ({misConstancias.length})</TabsTrigger>
          )}
        </TabsList>

        {/* ──────────────────── SESIONES ──────────────────── */}
        <TabsContent value="sesiones">
          <DataTable testid="table-sesiones" rows={sesionesVisibles} loading={loading}
            columns={[
              { key: "titulo", label: "Título" },
              { key: "tipo", label: "Tipo" },
              { key: "modalidad", label: "Modalidad" },
              { key: "fecha_programada", label: "Fecha" },
              { key: "duracion_horas", label: "Horas", render: (r) => `${r.duracion_horas || 0} h` },
              { key: "instructor_nombre", label: "Instructor" },
              { key: "estatus", label: "Estatus", render: (r) => <EstatusSesion e={r.estatus} /> },
              { key: "acciones", label: "", render: (r) => (
                <div className="flex gap-1">
                  {puedeInscribir && r.estatus === "Programada" && (
                    <Button size="sm" variant="outline" onClick={() => abrirInscripcion(r)} data-testid={`btn-inscribir-${r.id}`}>
                      <Users className="mr-1 h-3.5 w-3.5" /> Inscribir
                    </Button>
                  )}
                </div>
              )},
            ]}
            emptyLabel="Aún no hay sesiones asignadas por FELVORA."
          />
        </TabsContent>

        {/* ──────────────────── REPOSICIONES ──────────────────── */}
        <TabsContent value="reposiciones">
          <DataTable testid="table-reposiciones" rows={reposiciones} loading={loading}
            columns={[
              { key: "empleado_nombre", label: "Empleado" },
              { key: "empleado_puesto", label: "Puesto" },
              { key: "motivo", label: "Motivo" },
              { key: "medida_aplicada", label: "Medida" },
              { key: "estatus", label: "Estatus", render: (r) => {
                const map = { Pendiente: "bg-amber-100 text-amber-800", Reprogramada: "bg-blue-100 text-blue-800", Subsanada: "bg-emerald-100 text-emerald-800" };
                return <Badge className={map[r.estatus] || "bg-slate-100"}>{r.estatus}</Badge>;
              }},
              { key: "fecha_registro", label: "Registrada", render: (r) => (r.fecha_registro || "").slice(0, 10) },
              { key: "acciones", label: "", render: (r) => (
                puedeInscribir && r.estatus !== "Subsanada" ? (
                  <Button size="sm" variant="outline" onClick={async () => {
                    try {
                      await api.put(`/capacitacion/reposiciones/${r.id}`, { estatus: "Subsanada" });
                      toast.success("Reposición subsanada.");
                      await loadTodo();
                    } catch (e) { toast.error(apiError(e)); }
                  }} data-testid={`btn-subsanar-${r.id}`}>
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Marcar subsanada
                  </Button>
                ) : null
              )},
            ]}
            emptyLabel="Sin registros de reposición."
          />
        </TabsContent>

        {/* ──────────────────── CONSTANCIAS ──────────────────── */}
        <TabsContent value="constancias">
          <DataTable testid="table-constancias" rows={constancias} loading={loading}
            columns={[
              { key: "folio", label: "Folio" },
              { key: "nombre_participante", label: "Participante" },
              { key: "puesto", label: "Puesto" },
              { key: "titulo_curso", label: "Curso" },
              { key: "duracion_horas", label: "Horas", render: (r) => `${r.duracion_horas || 0} h` },
              { key: "calificacion", label: "Calif.", render: (r) => r.calificacion ?? "—" },
              { key: "fecha_impartida", label: "Impartida" },
              { key: "acciones", label: "", render: (r) => (
                <Button size="sm" variant="outline" onClick={() => descargarConstancia(r)}
                  disabled={descargando === r.id} data-testid={`btn-descargar-${r.id}`}>
                  {descargando === r.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
                  PDF
                </Button>
              )},
            ]}
            emptyLabel="Aún no hay constancias emitidas."
          />
        </TabsContent>

        {/* ──────────────────── MIS CONSTANCIAS ──────────────────── */}
        {esEmpleadoComun && (
          <TabsContent value="mis-constancias">
            <DataTable testid="table-mis-constancias" rows={misConstancias} loading={loading}
              columns={[
                { key: "folio", label: "Folio" },
                { key: "titulo_curso", label: "Curso" },
                { key: "duracion_horas", label: "Horas", render: (r) => `${r.duracion_horas || 0} h` },
                { key: "calificacion", label: "Calificación", render: (r) => r.calificacion ?? "—" },
                { key: "fecha_impartida", label: "Impartida" },
                { key: "acciones", label: "", render: (r) => (
                  <Button size="sm" variant="outline" onClick={() => descargarConstancia(r)}
                    disabled={descargando === r.id} data-testid={`btn-mia-${r.id}`}>
                    {descargando === r.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
                    PDF
                  </Button>
                )},
              ]}
              emptyLabel="No tienes constancias registradas. Si esperabas alguna, verifica con tu Oficial de Cumplimiento que el correo de tu registro en el padrón coincida con tu correo de acceso."
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Modal de inscripción */}
      <Dialog open={showInscribir} onOpenChange={setShowInscribir}>
        <DialogContent className="max-w-2xl" data-testid="dialog-inscribir">
          <DialogHeader>
            <DialogTitle>Inscribir personal · {sesionInscripcion?.titulo}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto rounded border border-slate-200 p-3">
            {padron.length === 0 && <p className="text-sm text-slate-500">No hay empleados activos en el padrón.</p>}
            {padron.map((e) => (
              <label key={e.id} className="flex cursor-pointer items-center gap-3 rounded border border-transparent px-2 py-2 hover:border-slate-200 hover:bg-slate-50"
                data-testid={`row-emp-${e.id}`}>
                <Checkbox checked={seleccion.has(e.id)} onCheckedChange={() => toggleSel(e.id)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-sm font-medium">{e.nombre_completo}</span>
                    {e.tiene_usuario
                      ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`badge-user-si-${e.id}`}><UserCheck className="mr-0.5 h-3 w-3" /> Con usuario</Badge>
                      : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-[10px]" data-testid={`badge-user-no-${e.id}`}><AlertTriangle className="mr-0.5 h-3 w-3" /> Sin usuario en plataforma</Badge>}
                    {e.atencion_al_publico && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Atención público</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {e.puesto || "—"} · {e.area || "—"} · {e.correo_electronico || "sin correo"}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            <strong>Con usuario</strong>: el empleado tiene acceso a la plataforma y podrá descargar su constancia en línea.
            &nbsp;<strong>Sin usuario</strong>: la constancia se emitirá y quedará disponible; el OC/admin deberá compartirla manualmente.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInscribir(false)}>Cancelar</Button>
            <Button onClick={enviarInscripcion} disabled={enviandoInsc || seleccion.size === 0}
              className="bg-forest-600 hover:bg-forest-700" data-testid="btn-inscribir-enviar">
              {enviandoInsc ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Users className="mr-1 h-4 w-4" />}
              Inscribir seleccionados ({seleccion.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
